// A thread's photos full size, over everything: the photo, who sent it and when, "3 / 7", download and close.
// Previous / next step through the thread's photos (buttons, the arrow keys, or a swipe on a touch screen);
// Escape, a click beside the photo and the phone's back gesture close it. The focus goes to Close on open and
// back to the photo that opened it. `fileUrl` serves photos kept elsewhere (a ticket's screenshots).
import { useEffect, useRef, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';
import { useDateFormatter } from '../lib/format.js';
import { useBackToClose } from '../lib/media.js';
import { IconChevronLeft, IconChevronRight, IconClose, IconDownload } from '../components/icons.js';
import { chatFileUrl } from './collabHooks.js';
import type { ThreadPhoto } from './chatFormat.js';

export function ChatLightbox({ photos, index, onIndex, onClose, fileUrl = chatFileUrl }: {
  photos: ThreadPhoto[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
  fileUrl?: (id: string, download?: boolean) => string;
}) {
  const df = useDateFormatter();
  const n = photos.length;
  const at = Math.min(Math.max(0, index), n - 1);
  const p = photos[at];
  const closeRef = useRef<HTMLButtonElement>(null);
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const step = (d: number) => { if (n > 1) onIndex((at + d + n) % n); };
  const stepRef = useRef(step);
  stepRef.current = step;
  useBackToClose(true, onClose);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => { if (opener?.isConnected) opener.focus({ preventScroll: true }); };
  }, []);
  // first in line for the keys while it's open, so Escape doesn't also close the chat behind it
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') stepRef.current(-1);
      else if (e.key === 'ArrowRight') stepRef.current(1);
      else return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, [onClose]);
  // the next and previous photos load while this one is looked at
  useEffect(() => {
    if (n < 2) return;
    for (const d of [1, -1]) {
      const q = photos[(at + d + n) % n];
      if (q) new Image().src = fileUrl(q.id);
    }
  }, [at, n, photos]);

  if (!p) return null;
  const onTouchStart = (e: TouchEvent) => { const t = e.touches[0]; swipe.current = t ? { x: t.clientX, y: t.clientY } : null; };
  const onTouchEnd = (e: TouchEvent) => {
    const s = swipe.current;
    const t = e.changedTouches[0];
    swipe.current = null;
    if (!s || !t) return;
    const dx = t.clientX - s.x;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(t.clientY - s.y) * 1.5) step(dx < 0 ? 1 : -1);
  };
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

  return createPortal(
    <div className="chat-lb" role="dialog" aria-modal="true" aria-label={`Photo ${at + 1} of ${n}`} onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="chat-lb-top" onClick={stop}>
        <span className="chat-lb-meta">
          <b>{p.from}</b>
          <span>{df.dateTime(p.at, { year: 'auto' })}</span>
        </span>
        {n > 1 && <span className="chat-lb-count">{at + 1} / {n}</span>}
        <a className="chat-lb-btn" href={fileUrl(p.id, true)} download={p.name} aria-label="Download" title="Download">
          <IconDownload size={19} />
        </a>
        <button ref={closeRef} type="button" className="chat-lb-btn" aria-label="Close" title="Close" onClick={onClose}>
          <IconClose size={20} />
        </button>
      </div>
      <img key={p.id} className="chat-lb-img" src={fileUrl(p.id)} alt={p.name} onClick={stop} />
      {n > 1 && (
        <>
          <button type="button" className="chat-lb-btn chat-lb-nav prev" aria-label="Previous photo" onClick={(e) => { stop(e); step(-1); }}>
            <IconChevronLeft size={22} />
          </button>
          <button type="button" className="chat-lb-btn chat-lb-nav next" aria-label="Next photo" onClick={(e) => { stop(e); step(1); }}>
            <IconChevronRight size={22} />
          </button>
        </>
      )}
    </div>,
    document.body,
  );
}
